import { ApolloError } from '@apollo/client/errors';
import { LoadingPage, Modal, ModalForm, TControlledFormComponentProps, TControlledFormProps } from '@chocolate-soup-inc/cs-frontend-components';
import { joiResolver } from '@hookform/resolvers/joi';
import { useCallback, useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getCompanyFormFields } from '../../entities/companies/formFields';
import { getCompanyDefaultValues, getCompanySchema } from '../../entities/companies/schema';
import { useListCognitoUsersNew } from '../../entities/users/queries';
import { TCreateCompanyMutationInput, useCreateCompanyMutation } from '../../generated/graphql';

export const CompanyCreate = () => {
  const navigate = useNavigate();
  const [createCompany] = useCreateCompanyMutation();

  const { data: adminCognitoUsersMap, loading } = useListCognitoUsersNew(import.meta.env.REACT_APP_COGNITO_USER_POOL_ID as string, true);

  const adminsUsersCurated = useMemo(
    () =>
      Object.values(adminCognitoUsersMap).map((admin: any) => ({
        label: `${admin.name} (${admin.email})`,
        value: `${admin.name} (${admin.email})`
      })),
    [adminCognitoUsersMap]
  );

  const closeModal = useCallback(() => {
    navigate(generatePath('../'));
  }, [navigate]);

  const onInvalidSubmit = useCallback<Exclude<TControlledFormProps<TCreateCompanyMutationInput>['onInvalidSubmit'], undefined>>((errors: Record<string, any>) => {
    console.error(errors);
    const submitError = errors[''];
    if (submitError) toast.error(submitError.message);
  }, []);

  const onSubmit = useCallback<TControlledFormProps<TCreateCompanyMutationInput>['onValidSubmit']>(
    (formData) => {
      return createCompany({
        variables: {
          input: formData
        }
      })
        .then(() => {
          toast.success('Successfully created the company.');
          closeModal();
        })
        .catch((error: ApolloError) => {
          toast.error('There was an error creating the company.');
          if (error instanceof ApolloError) error.graphQLErrors.forEach((err) => toast.error(err.message));
        });
    },
    [closeModal, createCompany]
  );

  if (loading)
    return (
      <Modal closeModal={closeModal} headline={'New Company'}>
        <LoadingPage />
      </Modal>
    );

  return (
    <ModalForm
      headline='New Company'
      size='large'
      closeModal={closeModal}
      controlledFormProps={{
        fields: getCompanyFormFields(undefined, adminsUsersCurated) as TControlledFormComponentProps<TCreateCompanyMutationInput>[],
        formProps: {
          defaultValues: getCompanyDefaultValues(),
          resolver: joiResolver(getCompanySchema(), {
            convert: true,
            abortEarly: false,
            stripUnknown: false
          })
        },
        onValidSubmit: onSubmit,
        onInvalidSubmit: onInvalidSubmit
      }}
    />
  );
};
